/**
 * FluidCloud picker — import an image you already have in your Drive.
 *
 * A thin typed wrapper over FluidCloud's dependency-free `picker.js`, served
 * from the cloud web origin. It opens the file-picker widget; the selection
 * comes back as short-lived (~30 min) download URLs carrying
 * `Access-Control-Allow-Origin: *`, so the browser can fetch the bytes and hand
 * them to our normal upload path.
 *
 * FluidDream is a first-party Fluid app, so there's no clientId — the cloud
 * validates our ORIGIN against its first-party allowlist. If the picker refuses
 * to open, that allowlist is the first thing to check.
 *
 * Mirrors FluidGhost's web/src/lib/fluidcloudPicker.ts.
 */
import { FLUIDCLOUD_WEB_URL } from './env';

/** One picked file. `url` expires in roughly 30 minutes. */
export interface PickedFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  url: string;
  expires_at: string;
}

interface FluidCloudPickerApi {
  open: (opts: {
    clientId?: string;
    multiple?: boolean;
    accept?: string;
    mode?: 'iframe' | 'popup';
    onPicked?: (items: PickedFile[]) => void;
    onCancel?: () => void;
  }) => { close: () => void };
  origin: string;
}

declare global {
  interface Window {
    FluidCloudPicker?: FluidCloudPickerApi;
  }
}

/** Cached so picker.js is injected at most once per page. */
let loaderPromise: Promise<FluidCloudPickerApi> | null = null;

function loadPicker(): Promise<FluidCloudPickerApi> {
  if (window.FluidCloudPicker) return Promise.resolve(window.FluidCloudPicker);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise<FluidCloudPickerApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${FLUIDCLOUD_WEB_URL}/picker.js`;
    script.async = true;
    script.onload = () => {
      if (window.FluidCloudPicker) resolve(window.FluidCloudPicker);
      else reject(new Error('FluidCloud opened but the picker did not start.'));
    };
    script.onerror = () => {
      loaderPromise = null; // let the next click retry
      reject(new Error('Could not reach FluidCloud to open the picker.'));
    };
    document.head.appendChild(script);
  });
  return loaderPromise;
}

export interface OpenPickerOptions {
  multiple?: boolean;
  accept?: string;
  onPicked: (items: PickedFile[]) => void;
  onCancel?: () => void;
}

export async function openFluidCloudPicker(opts: OpenPickerOptions): Promise<void> {
  const picker = await loadPicker();
  picker.open({
    multiple: opts.multiple ?? true,
    accept: opts.accept ?? 'image/*',
    onPicked: (items) => opts.onPicked(items ?? []),
    ...(opts.onCancel ? { onCancel: opts.onCancel } : {}),
  });
}

/**
 * Fetch a picked file into a File the upload path can take. The picked URL is
 * short-lived and CORS-open by design, so this runs in the browser rather than
 * handing the URL to our API — which would mean the API trusting a URL, exactly
 * what the render route refuses to do.
 */
export async function fetchPicked(item: PickedFile): Promise<File> {
  const res = await fetch(item.url);
  if (!res.ok) {
    throw new Error(
      res.status === 403
        ? 'That FluidCloud link has expired — pick the file again.'
        : `Could not download “${item.name}” from FluidCloud (${res.status}).`,
    );
  }
  const blob = await res.blob();
  return new File([blob], item.name || 'fluidcloud-image', {
    type: item.mime || blob.type || 'image/jpeg',
  });
}
